import _ from 'lodash';
import React from 'react';
import PropTypes from 'prop-types';
import styled, { ThemeContext } from 'styled-components';
import classNames from 'classnames';
import { useSpring, animated } from 'react-spring';
import FundFollowButton from './fund-follow-button';
import useFundStateStore from '../common/fund-state-hook';
import IconedButton from '../../shared/iconed-button';
import EnquiryContext from '../../shared/enquiry/context';
import FundNameTwoLines from './fund-name-two-lines';
import { recordStatisticsEvent } from '../../../services/statistics-service';

const height = 68;
const Container = styled(animated.div)`
  z-index: 100;
  width: 100vw;
  height: ${height}px;
  background: white;
  position: fixed;
  bottom: 0px;
  box-shadow: 0px -4px 15px rgba(0, 0, 0, 0.15);
  display: flex;
  align-items: center;
  padding: 10px 20px 22px 20px;

  & > button {
    margin: 0;
    margin-left: 8px;
    height: 36px;
    align-items: center;
    outline: none;
  }

  &:not(.discreet) > button:not([name="fund-follow-button"]) {
    width: 33px;
    min-width: 33px;
    padding: 0;
    background-color: ${props => props.theme.bodyColor};

    > span:not(.icon-lw) {
      display: none;
    }
  }


  &&.discreet {
    > button[name="fund-follow-button"] {
      display: none;
    }

    > button:not([name="fund-follow-button"]) {
      background-color: #3c4966;
      min-width: 107px;
      width: 107px;

      > span.icon-lw-contact {
        padding: 3px;
      }
    }
  }

  &&&& > button:not([name="fund-follow-button"]) {
    color: white;

    > span.icon-lw-contact {
      background-color: ${props => props.theme.bodyColor};
    }
  }

`;

export default function StickyFooter({
  fund, yOffset, showContactButton,
}) {
  const { isMobile } = React.useContext(ThemeContext);
  const { dispatch } = React.useContext(EnquiryContext);
  const [discreet, setDiscreet] = React.useState(fund.isFollowedByCurrentUser);
  const following = useFundStateStore(state => state.followed);

  const isVisible = React.useCallback(
    () => ((!discreet || showContactButton) && (yOffset > (isMobile ? 250 : 290))),
    [yOffset, discreet, showContactButton, isMobile],
  );

  const styles = useSpring({
    opacity: isVisible() ? 1 : 0,
    transform: `translateY(${isVisible() ? 0 : height}px)`,
  });

  const contact = e => {
    e && e.preventDefault();

    recordStatisticsEvent({
      event: 'mobile_footer_contact_fund',
      statisticable: 'fund',
      id: fund.id,
      page: window.location.pathname,
    });

    if (_.get(window, 'Livewire.currentUser.id')) {
      dispatch({ type: 'showEnquiryForm', payload: { scroll: true, offset: -200 } });
    } else {
      window.location = '/sign_up?return_to_section=enquiry';
    }
  };

  React.useEffect(() => {
    setDiscreet(following && fund.isFollowedByCurrentUser);
  }, [following, fund.isFollowedByCurrentUser]);

  return (
    <Container style={styles} className={classNames({ discreet })}>
      <FundNameTwoLines text={fund.name} />
      <FundFollowButton fund={fund} />
      {!!showContactButton && (
        <IconedButton type="button" className="btn" onClick={contact}>
          <span className="icon-lw icon-lw-contact" />
          <span>contact</span>
        </IconedButton>
      )}
    </Container>
  );
}

StickyFooter.propTypes = {
  fund: PropTypes.shape({
    id: PropTypes.number,
    name: PropTypes.string,
    isFollowedByCurrentUser: PropTypes.bool,
  }).isRequired,
  showContactButton: PropTypes.bool.isRequired,
  yOffset: PropTypes.number.isRequired,
};
